/**
 * validateActivity - Runtime validation for activity definitions.
 *
 * Ported and extended from bumblebee validateLevel.js.
 * Validates activity JSON against the universal activity schema
 * without requiring a JSON Schema library dependency.
 *
 * @see GitHub Issue #852
 */

const VALID_TYPES = ['challenge', 'guided-creation', 'free-create', 'explore'];
const VALID_TIERS = ['free', 'premium'];
const VALID_DIFFICULTIES = ['foundational', 'beginner', 'intermediate', 'advanced'];
const KEBAB_CASE = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;
const CUSTOM_CHALLENGE_FLOW_KEYS = ['storyWalk', 'bugBand', 'moonwalkMachine', 'treasureMap'];

/**
 * Validate an activity definition.
 * @param {object} data - Activity JSON data
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateActivity (data) {
    const errors = [];

    if (!data || typeof data !== 'object') {
        return {valid: false, errors: ['Activity data must be an object']};
    }

    // Required top-level fields
    _requireString(data, 'activityId', errors);
    if (data.activityId && !KEBAB_CASE.test(data.activityId)) {
        errors.push(`activityId "${data.activityId}" must be kebab-case`);
    }

    if (!data.type || !VALID_TYPES.includes(data.type)) {
        errors.push(`type must be one of: ${VALID_TYPES.join(', ')}`);
    }

    if (!data.tier || !VALID_TIERS.includes(data.tier)) {
        errors.push(`tier must be one of: ${VALID_TIERS.join(', ')}`);
    }

    // i18n text fields
    _validateI18n(data, 'title', errors);
    _validateI18n(data, 'instructions', errors);

    // Curriculum reference
    _validateCurriculum(data.curriculum, errors);

    // Project template
    _validateProjectTemplate(data.projectTemplate, errors);

    // Success criteria (type-specific)
    _validateSuccessCriteria(data, errors);

    // Scaffolding
    _validateScaffolding(data.type, data.scaffolding, errors);

    // Metadata
    _validateMetadata(data.metadata, errors);

    // Assets (optional)
    if (data.assets) {
        _validateAssets(data.assets, errors);
    }

    return {valid: errors.length === 0, errors};
}

/**
 * Validate a curriculum manifest.
 * @param {object} manifest
 * @returns {{valid: boolean, errors: string[]}}
 */
export function validateManifest (manifest) {
    const errors = [];

    if (!manifest || typeof manifest !== 'object') {
        return {valid: false, errors: ['Manifest must be an object']};
    }

    // Top-level curriculum manifest
    if (manifest.courses) {
        if (!Array.isArray(manifest.courses)) {
            errors.push('courses must be an array');
        } else {
            manifest.courses.forEach((c, i) => {
                if (!c.courseId) errors.push(`courses[${i}] missing courseId`);
                if (!c.title) errors.push(`courses[${i}] missing title`);
            });
        }
        return {valid: errors.length === 0, errors};
    }

    // Course-level manifest
    if (!manifest.courseId) errors.push('Missing courseId');
    if (!manifest.title) errors.push('Missing title');

    if (manifest.units) {
        if (!Array.isArray(manifest.units)) {
            errors.push('units must be an array');
        } else {
            manifest.units.forEach((u, ui) => {
                if (!u.unitId) errors.push(`units[${ui}] missing unitId`);
                if (!u.title) errors.push(`units[${ui}] missing title`);
                if (!Array.isArray(u.lessons)) {
                    errors.push(`units[${ui}] missing lessons array`);
                } else {
                    u.lessons.forEach((l, li) => {
                        if (!l.activityId) errors.push(`units[${ui}].lessons[${li}] missing activityId`);
                        if (!l.file) errors.push(`units[${ui}].lessons[${li}] missing file`);
                    });
                }
            });
        }
    }

    return {valid: errors.length === 0, errors};
}

// --- Private validators ---

function _requireString (obj, field, errors) {
    if (!obj[field] || typeof obj[field] !== 'string') {
        errors.push(`Missing or invalid ${field} (must be a string)`);
    }
}

function _validateI18n (obj, field, errors) {
    if (!obj[field] || typeof obj[field] !== 'object') {
        errors.push(`${field} must be an i18n object`);
        return;
    }
    if (!obj[field].en || typeof obj[field].en !== 'string') {
        errors.push(`${field}.en is required`);
    }
}

function _validateCurriculum (curriculum, errors) {
    if (!curriculum || typeof curriculum !== 'object') {
        errors.push('curriculum is required');
        return;
    }
    if (!curriculum.courseId) errors.push('curriculum.courseId is required');
    if (!curriculum.unitId) errors.push('curriculum.unitId is required');
    if (typeof curriculum.lessonNumber !== 'number') errors.push('curriculum.lessonNumber must be a number');
}

function _validateProjectTemplate (template, errors) {
    if (!template || typeof template !== 'object') {
        errors.push('projectTemplate is required');
        return;
    }
    if (!Array.isArray(template.pages) || template.pages.length === 0) {
        errors.push('projectTemplate.pages must be a non-empty array');
        return;
    }
    template.pages.forEach((page, i) => {
        if (!page.background) {
            errors.push(`projectTemplate.pages[${i}] missing background`);
        }
        if (page.sprites && !Array.isArray(page.sprites)) {
            errors.push(`projectTemplate.pages[${i}].sprites must be an array`);
        }
    });
}

function _validateSuccessCriteria (data, errors) {
    const type = data && data.type;
    const criteria = data && data.successCriteria;
    if (!criteria || typeof criteria !== 'object') {
        errors.push('successCriteria is required');
        return;
    }

    switch (type) {
    case 'challenge':
        // Dedicated challenge flows manage their own success semantics and do not
        // rely on the generic coordinate goal validator.
        if (!_hasCustomChallengeFlow(data)) {
            if (typeof criteria.goalX !== 'number') errors.push('successCriteria.goalX required for challenge');
            if (typeof criteria.goalY !== 'number') errors.push('successCriteria.goalY required for challenge');
        }
        break;
    case 'guided-creation':
        if (typeof criteria.requiredSteps !== 'number') {
            errors.push('successCriteria.requiredSteps required for guided-creation');
        }
        break;
    case 'free-create':
        if (!Array.isArray(criteria.rubric)) {
            errors.push('successCriteria.rubric required for free-create');
        }
        break;
    case 'explore':
        if (!Array.isArray(criteria.discoveries)) {
            errors.push('successCriteria.discoveries required for explore');
        }
        break;
    }
}

function _hasCustomChallengeFlow (data) {
    if (!data || typeof data !== 'object') {
        return false;
    }

    return CUSTOM_CHALLENGE_FLOW_KEYS.some((key) => Object.prototype.hasOwnProperty.call(data, key));
}

function _validateScaffolding (type, scaffolding, errors) {
    if (!scaffolding || typeof scaffolding !== 'object') {
        errors.push('scaffolding is required');
        return;
    }

    if (scaffolding.availableBlocks && !Array.isArray(scaffolding.availableBlocks)) {
        errors.push('scaffolding.availableBlocks must be an array');
    }

    if (scaffolding.hints && !Array.isArray(scaffolding.hints)) {
        errors.push('scaffolding.hints must be an array');
    }

    // Guided creation requires steps
    if (type === 'guided-creation') {
        if (!Array.isArray(scaffolding.steps) || scaffolding.steps.length === 0) {
            errors.push('scaffolding.steps required for guided-creation activities');
        } else {
            scaffolding.steps.forEach((step, i) => {
                if (!step.stepId) errors.push(`scaffolding.steps[${i}] missing stepId`);
                if (!step.targetBlock) errors.push(`scaffolding.steps[${i}] missing targetBlock`);
                if (!step.successCheck) errors.push(`scaffolding.steps[${i}] missing successCheck`);
            });
        }
    }
}

function _validateMetadata (metadata, errors) {
    if (!metadata || typeof metadata !== 'object') {
        errors.push('metadata is required');
        return;
    }
    if (metadata.difficulty && !VALID_DIFFICULTIES.includes(metadata.difficulty)) {
        errors.push(`metadata.difficulty must be one of: ${VALID_DIFFICULTIES.join(', ')}`);
    }
    if (metadata.estimatedMinutes && typeof metadata.estimatedMinutes !== 'number') {
        errors.push('metadata.estimatedMinutes must be a number');
    }
}

function _validateAssets (assets, errors) {
    if (typeof assets !== 'object') {
        errors.push('assets must be an object');
        return;
    }
    ['backgrounds', 'sprites', 'sounds'].forEach(key => {
        if (assets[key] && !Array.isArray(assets[key])) {
            errors.push(`assets.${key} must be an array`);
        }
    });
}
